import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useUserStore } from '@/store/useUserStore';
import { canAccessPath } from '@/lib/permission';
import type { UserRole } from '@/types/userType';

interface ProtectedRouteProps {
  // Legacy per-route role hint kept for documentation. Actual enforcement is
  // navConfig-driven (canAccessPath) so it can never drift from the sidebar the
  // user actually sees — the route-level lists had grown stale/inconsistent.
  allowedRoles?: UserRole[];
}

/**
 * ProtectedRoute
 *
 * Gates a group of routes by role + permission. This tree is already wrapped by
 * <AuthGuard/> (which handles "not logged in" and token expiry), so here we only
 * decide whether the authenticated user may reach THIS path.
 *
 * Access is judged with the same rules that build the sidebar/search
 * (`canAccessPath` over `navConfig`, including sub-item precision), evaluated
 * across the user's effective roles. Super admins bypass the fine-grained gate,
 * mirroring `usePermissions` / `useFilteredNav`. A denial routes to
 * /access-denied, which eases the user back to their first accessible page.
 */
export const ProtectedRoute = ({ allowedRoles }: ProtectedRouteProps) => {
  void allowedRoles;

  const role = useUserStore((state) => state.role);
  const user = useUserStore((state) => state.user);
  const isSuperAdmin = useUserStore((state) => state.isSuperAdmin);
  const isPartner = useUserStore((state) => state.isPartner);
  const location = useLocation();

  // Defensive: never render a protected page for a logged-out user, even though
  // AuthGuard should already have redirected.
  if (!role || !user) {
    return <Navigate to="/" replace state={{ from: location.pathname + location.search }} />;
  }

  // Super admins can reach any in-app page (same bypass as the sidebar).
  if (isSuperAdmin) {
    return <Outlet />;
  }

  // Effective roles mirror useFilteredNav / useCanAccessPath: honour the active
  // role plus the central "partner" flag (a partner's derived role is usually
  // "admin", so partner-gated routes need it surfaced explicitly).
  const effectiveRoles: UserRole[] =
    isPartner && role !== 'partner'
      ? [role as UserRole, 'partner']
      : [role as UserRole];

  const permissions = user.permissions ?? [];
  const canAccess = effectiveRoles.some((r) =>
    canAccessPath(location.pathname, r, permissions),
  );

  if (!canAccess) {
    return <Navigate to="/access-denied" replace />;
  }

  return <Outlet />;
};
